/*
 * ChipTimePicker · chip 点击后的时间范围选择弹窗
 *
 * 独立自包含,与 UserAskBlock / UserAskActionSheet **完全解耦**(不再借用它们的 SCSS
 * 类名或 DOM 结构),视觉上仍是 ActionSheet 那种"底部浮层"节奏(portal 到 body,
 * 800 宽 12 圆角,60px 离底,不贴屏)。
 *
 * 用户交互:
 *   - 6 个时间预设 3×2 网格(今日/本周/本月/上月/最近 7 天/最近 30 天),默认本月
 *   - 自定义时间段单独一行,选中展开 RangePicker
 *   - 确认按钮(紫)/ 取消,居右
 *
 * API:onConfirm 只回 { start, end, label },chip.message 拼接放在业务侧(HomeV3)。
 */

import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, DatePicker, message as msg } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import './index.scss'

/** 对外返回的时间区间;busy 状态由外部控制(通常是 uploading) */
export interface TimeRange {
  start: Dayjs
  end: Dayjs
  /** 用户看到的口径(如"本月""自定义时段"),回显给 HomeV3 生成 label 用 */
  label: string
}

interface ChipTimePickerProps {
  /** 打开 = truthy(通常传 pendingChip 或布尔),关闭 = null/false */
  open: boolean
  /** 标题上方一行小字副标题,如"数据统计的时间范围" */
  subtitle?: string
  /** 主标题,通常带 chip label,如「安全巡检记录表」用哪段时间的数据? */
  title: string
  /** 上传中,禁点关/取消/确认;确认按钮进 loading */
  busy?: boolean
  onClose: () => void
  onConfirm: (range: TimeRange) => void
}

type PresetKey =
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'last_7'
  | 'last_30'
  | 'custom'

interface Preset {
  key: PresetKey
  label: string
}

// 6 个预设 + 1 个自定义;上面 6 个 3×2 网格,自定义单独一行
const PRESETS_GRID: Preset[] = [
  { key: 'today', label: '今日' },
  { key: 'this_week', label: '本周' },
  { key: 'this_month', label: '本月' },
  { key: 'last_month', label: '上月' },
  { key: 'last_7', label: '最近 7 天' },
  { key: 'last_30', label: '最近 30 天' }
]
const CUSTOM_PRESET: Preset = { key: 'custom', label: '自定义时间段' }

/** preset key → 具体 dayjs 区间(含头含尾,按天粒度) */
function resolveRange(
  key: PresetKey,
  customRange: [Dayjs, Dayjs] | null
): TimeRange {
  const today = dayjs()
  switch (key) {
    case 'today':
      return {
        start: today.startOf('day'),
        end: today.endOf('day'),
        label: '今日'
      }
    case 'this_week':
      return {
        start: today.startOf('week'),
        end: today.endOf('week'),
        label: '本周'
      }
    case 'this_month':
      return {
        start: today.startOf('month'),
        end: today.endOf('month'),
        label: '本月'
      }
    case 'last_month':
      return {
        start: today.subtract(1, 'month').startOf('month'),
        end: today.subtract(1, 'month').endOf('month'),
        label: '上月'
      }
    case 'last_7':
      return {
        start: today.subtract(6, 'day').startOf('day'),
        end: today.endOf('day'),
        label: '最近 7 天'
      }
    case 'last_30':
      return {
        start: today.subtract(29, 'day').startOf('day'),
        end: today.endOf('day'),
        label: '最近 30 天'
      }
    case 'custom':
      if (customRange) {
        return {
          start: customRange[0].startOf('day'),
          end: customRange[1].endOf('day'),
          label: '自定义时段'
        }
      }
      return {
        start: today.startOf('month'),
        end: today.endOf('month'),
        label: '本月'
      }
  }
}

const ChipTimePicker: React.FC<ChipTimePickerProps> = ({
  open,
  subtitle,
  title,
  busy,
  onClose,
  onConfirm
}) => {
  // 弹窗每次打开都重置为「本月」默认,避免跨 chip 记住上次选择造成误发。
  // 通过 open 从 false→true 时(初次 mount + 重新 mount 都走 useState 初值)。
  const [pickedKey, setPickedKey] = useState<PresetKey>('this_month')
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null)

  const handleClose = useCallback(() => {
    if (busy) return
    onClose()
  }, [busy, onClose])

  const handleConfirm = useCallback(() => {
    if (pickedKey === 'custom' && !customRange) {
      msg.warning('请选择自定义时间段')
      return
    }
    onConfirm(resolveRange(pickedKey, customRange))
  }, [pickedKey, customRange, onConfirm])

  const confirmDisabled = useMemo(
    () => pickedKey === 'custom' && !customRange,
    [pickedKey, customRange]
  )

  if (!open) return null

  const body = (
    <>
      {/* 半透明遮罩,点空关掉(busy 时不响应) */}
      <div
        className="aiClerk-chipTimePicker-mask"
        aria-hidden="true"
        onClick={handleClose}
      />
      {/* 底部浮层(portal),自包含,不借 UserAskActionSheet 的类 */}
      <div className="aiClerk-chipTimePicker" role="dialog" aria-modal="true">
        <button
          type="button"
          className="aiClerk-chipTimePicker__close"
          onClick={handleClose}
          disabled={busy}
          aria-label="关闭"
        >
          ✕
        </button>
        <div className="aiClerk-chipTimePicker__body">
          <div className="aiClerk-chipTimePicker__head">
            {subtitle && (
              <div className="aiClerk-chipTimePicker__sub">{subtitle}</div>
            )}
            <div className="aiClerk-chipTimePicker__title">{title}</div>
          </div>

          {/* 上面 6 个预设 3×2 网格 */}
          <div className="aiClerk-chipTimePicker__grid">
            {PRESETS_GRID.map((p) => {
              const checked = pickedKey === p.key
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`aiClerk-chipTimePicker__opt${
                    checked ? ' is-checked' : ''
                  }`}
                  onClick={() => setPickedKey(p.key)}
                  disabled={busy}
                >
                  {p.label}
                </button>
              )
            })}
          </div>

          {/* 自定义时间段:整宽 + 铅笔 icon,选中展开 RangePicker */}
          <button
            type="button"
            className={`aiClerk-chipTimePicker__opt aiClerk-chipTimePicker__opt--custom${
              pickedKey === 'custom' ? ' is-checked' : ''
            }`}
            onClick={() => setPickedKey('custom')}
            disabled={busy}
          >
            <span className="aiClerk-chipTimePicker__opt-icon" aria-hidden>
              <EditOutlined />
            </span>
            <span>{CUSTOM_PRESET.label}</span>
          </button>
          {pickedKey === 'custom' && (
            <div className="aiClerk-chipTimePicker__range-wrap">
              <DatePicker.RangePicker
                className="aiClerk-chipTimePicker__range-picker"
                value={customRange}
                onChange={(v) =>
                  setCustomRange(v && v[0] && v[1] ? [v[0], v[1]] : null)
                }
                disabled={busy}
                allowClear={false}
                format="YYYY-MM-DD"
                style={{ width: '100%', height: 40 }}
              />
            </div>
          )}

          {/* actions 居右,静态定位(内容短不需要 sticky) */}
          <div className="aiClerk-chipTimePicker__actions">
            <Button onClick={handleClose} disabled={busy}>
              取消
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              loading={busy}
              disabled={confirmDisabled}
              className="aiClerk-chipTimePicker__submit"
            >
              发送
            </Button>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(body, document.body)
}

export default ChipTimePicker
